import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, TrendingUp, Receipt, Plus } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { fmtCents } from '../../lib/financial';
import EmptyState from '../../components/crm/EmptyState';
import NewInvoiceDialog from '../../components/admin/NewInvoiceDialog';

// Admin-only financial dashboard. Ops/client route-gated (the backend
// middleware 403s the data fetch; the UI also hides the navigation link).
export default function Financial() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [aging, setAging] = useState(null);
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [d, a] = await Promise.all([
        api.get('/api/admin/financial/dashboard'),
        api.get('/api/admin/financial/aging').catch(() => null),
      ]);
      setDashboard(d);
      setAging(a);
    } catch (err) {
      setError(err?.message || 'Failed to load financial dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user?.role !== 'nexsav_admin') {
    return (
      <div className="max-w-3xl mx-auto mt-12">
        <EmptyState icon={AlertCircle} title="Admin only"
          description="Financial views are restricted to Nexsav admins." />
      </div>
    );
  }

  const mtdBooked = dashboard?.revenue?.bookedThisMonthCents ?? 0;
  const mtdCollected = dashboard?.revenue?.collectedThisMonthCents ?? 0;
  const pmBooked = dashboard?.revenue?.bookedPrevMonthCents ?? 0;
  const pmCollected = dashboard?.revenue?.collectedPrevMonthCents ?? 0;
  const momBooked = pmBooked ? ((mtdBooked - pmBooked) / pmBooked) * 100 : null;
  const momCollected = pmCollected ? ((mtdCollected - pmCollected) / pmCollected) * 100 : null;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Financial</span></h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Accounts receivable + revenue at a glance.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setNewInvoiceOpen(true)}
            data-testid="financial-new-invoice"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900"
          >
            <Plus className="w-3 h-3" /> New invoice
          </button>
          <button onClick={() => navigate('/admin/invoices')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] text-gray-300 hover:text-white bg-white/[0.03] border border-white/[0.08]">
            <Receipt className="w-3 h-3" /> All invoices
          </button>
        </div>
      </div>

      {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-3">{error}</div>}

      {loading && !dashboard ? <div className="animate-pulse h-28 rounded-xl bg-white/[0.03]" /> : dashboard && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <Stat label="Outstanding AR" primary={fmtCents(dashboard.ar.outstandingCents)} subtitle={`${fmtCents(dashboard.ar.paidCents)} of ${fmtCents(dashboard.ar.billedCents)} collected`} />
            <Stat label="Overdue invoices" primary={String(dashboard.overdueCount)} subtitle={dashboard.overdueCount === 0 ? 'clean' : 'sent + past due'} tone={dashboard.overdueCount > 0 ? 'alert' : null} />
            <Stat
              label="Booked this month"
              primary={fmtCents(mtdBooked)}
              subtitle={momBooked != null ? `${momBooked >= 0 ? '+' : ''}${momBooked.toFixed(1)}% vs prev month` : 'no prev-month data'}
              icon={TrendingUp}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            <Stat label="Collected this month" primary={fmtCents(mtdCollected)} subtitle={momCollected != null ? `${momCollected >= 0 ? '+' : ''}${momCollected.toFixed(1)}% vs prev month` : 'no prev-month data'} />
            <Stat label="Prev month collected" primary={fmtCents(pmCollected)} subtitle={`Booked ${fmtCents(pmBooked)}`} />
          </div>

          {aging?.buckets && (
            <div data-testid="ar-aging-row" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {['0-30', '31-60', '61-90', '90+'].map((b) => (
                <button
                  key={b}
                  onClick={() => navigate(`/admin/invoices?aging=${encodeURIComponent(b)}`)}
                  data-testid={`aging-bucket-${b}`}
                  className="text-left rounded-2xl border border-white/[0.05] bg-white/[0.015] p-4 hover:bg-white/[0.025] transition-colors"
                >
                  <div className="text-[11px] text-gray-500 uppercase tracking-wider">Aging {b}</div>
                  <div className="text-[18px] text-white font-semibold mt-1">{fmtCents(aging.buckets[b]?.total_cents || 0)}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">{aging.buckets[b]?.count || 0} invoice{(aging.buckets[b]?.count || 0) === 1 ? '' : 's'}</div>
                </button>
              ))}
            </div>
          )}

          <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5">
            <h3 className="text-[13px] font-semibold text-white mb-3">Top open balances</h3>
            {dashboard.topOutstandingAccounts.length === 0 ? (
              <div className="text-[12px] text-gray-500">No outstanding balances. Clean slate.</div>
            ) : (
              <div className="space-y-2">
                {dashboard.topOutstandingAccounts.map((a) => (
                  <button key={a.accountId} onClick={() => navigate(`/admin/accounts/${a.accountId}?tab=financial`)}
                    className="w-full text-left p-3 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] transition-colors flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[13px] text-white truncate">{a.accountName}</div>
                      <div className="text-[11px] text-gray-500">{a.accountSlug}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[13px] text-white font-medium">{fmtCents(a.outstandingCents)}</div>
                      <div className="text-[10px] text-gray-500">of {fmtCents(a.billedCents)}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {newInvoiceOpen && (
        <NewInvoiceDialog
          open
          onClose={() => setNewInvoiceOpen(false)}
          onCreated={(inv) => {
            setNewInvoiceOpen(false);
            if (inv?.id) navigate(`/admin/invoices/${inv.id}`);
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, primary, subtitle, icon: Icon = null, tone = null }) {
  const toneCls = tone === 'alert' ? 'border-red-500/20 bg-red-500/5' : 'border-white/[0.05] bg-white/[0.015]';
  return (
    <div className={`rounded-2xl border p-5 ${toneCls}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] text-gray-500 uppercase tracking-wider">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-gray-500" />}
      </div>
      <div className="text-[22px] text-white font-semibold">{primary}</div>
      {subtitle && <div className="text-[11px] text-gray-500 mt-1">{subtitle}</div>}
    </div>
  );
}
